import { ApiError, errorEnvelope } from './http';
import { ApiErrorCodes } from './errors';
import { toWire } from './wire';
import { mapRemindForDueKind } from '@/features/tasks/remind';
import type { TasksApi } from './contract';
import {
  TASK_LIST_COLORS,
  TASK_REMIND_DATE_ONLY,
  TASK_REMIND_TIMED,
  type Task,
  type TaskEmailLink,
  type TaskEmailResolution,
  type TaskInput,
  type TaskList,
  type TaskListColor,
  type TaskPatch,
  type TaskRemind,
} from './types';

/**
 * MOCK: task lists and tasks (ADR 0069), for `VITE_USE_MOCKS=true` and tests.
 *
 * It keeps the server's rules rather than being kinder than it (the HANDOFF
 * lesson — a lenient fake once hid a foreign-key bug): every id resolves
 * inside ONE account and anything else is a 404, a PATCH must carry the
 * version it was based on, the caps answer 409 `limit`, and deleting a list
 * takes its tasks with it. Positions are gap integers, as on the server, and
 * never leave this file.
 *
 * Subtasks and reminders keep the migration's rules too
 * (`20261002090000_task_subtasks_reminders.sql`): one level, the parent a
 * top-level task of the same list; a parent's subtasks go with it when it is
 * deleted or moved to another list; a subtask moved to another list becomes
 * top-level there; reopening a subtask reopens its completed parent. A
 * reminder needs a due date of the matching kind, follows a change of kind
 * the way the server maps it, and its instant is computed here in the
 * browser's zone (the server uses the owner's; for a mock the two agree).
 *
 * `accountExists` is the mock's stand-in for the foreign key to
 * `connected_accounts`: a list cannot be made for an account that is not
 * there.
 *
 * A task made from an email (ADR 0075) keeps where the email was and its
 * Message-ID, as the server does. `mail` is the mock mailbox the server
 * would ask over IMAP: where a message is now, by its id or its Message-ID.
 */

/** The mock mailbox, as the task link sees it. */
export interface MockTaskMail {
  /** The message with this app id in this mailbox, wherever it is now. */
  byId(accountId: string, messageId: string): MockMailRef | undefined;
  /** The message with this Message-ID in this mailbox. */
  byRfcId(accountId: string, rfcMessageId: string): MockMailRef | undefined;
}

export interface MockMailRef {
  messageId: string;
  folderId: string;
  folderRole: string;
  rfcMessageId: string;
}

export const MOCK_MAX_TASK_LISTS = 100;
export const MOCK_MAX_TASKS_PER_LIST = 5000;
const MAX_NAME = 100;
const MAX_TITLE = 500;
const MAX_NOTES = 8000;
const STEP = 65536;
const SMART_LIMIT = 500;

interface ListRow {
  accountId: string;
  list: Omit<TaskList, 'openCount' | 'completedCount'>;
  position: number;
}

interface TaskRow {
  accountId: string;
  task: Task;
  /** Among its siblings: top-level tasks of the list, or one parent's subtasks. */
  position: number;
  /** When the reminder stops being worth sending; server-only, never on the wire. */
  remindUntil: number | null;
}

export interface MockTaskSeed {
  accountId: string;
  name: string;
  color: TaskListColor;
  tasks: Array<Partial<TaskInput> & { title: string; completed?: boolean }>;
}

let requestSeq = 0;
function fail(status: number, code: string, message: string, details?: Record<string, unknown>): ApiError {
  requestSeq += 1;
  return new ApiError(status, errorEnvelope(code, message, details, `mock-tasks-${requestSeq}`));
}
const notFound = (what: string) => fail(404, ApiErrorCodes.NotFound, `No such ${what}.`);
const invalid = (message: string) => fail(400, ApiErrorCodes.BadRequest, message);

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

function checkDate(value: string | null | undefined): void {
  if (value === undefined || value === null) return;
  if (!DATE.test(value) || Number.isNaN(Date.parse(`${value}T00:00:00Z`))) {
    throw invalid('due_date must be a date written YYYY-MM-DD.');
  }
}
function checkTime(value: string | null | undefined): void {
  if (value === undefined || value === null) return;
  if (!TIME.test(value)) throw invalid('due_time must be a time written HH:MM.');
}
function checkTitle(title: string): string {
  const trimmed = title.trim();
  if (trimmed === '') throw invalid('A task needs a title.');
  if ([...trimmed].length > MAX_TITLE) throw invalid(`A title can be at most ${MAX_TITLE} characters.`);
  return trimmed;
}
function checkNotes(notes: string): void {
  if ([...notes].length > MAX_NOTES) throw invalid(`Notes can be at most ${MAX_NOTES} characters.`);
}
function checkName(name: string): string {
  const trimmed = name.trim();
  if (trimmed === '') throw invalid('A list needs a name.');
  if ([...trimmed].length > MAX_NAME) throw invalid(`A list name can be at most ${MAX_NAME} characters.`);
  return trimmed;
}
function checkColor(color: string | undefined): void {
  if (color === undefined) return;
  if (!(TASK_LIST_COLORS as readonly string[]).includes(color)) {
    throw invalid(`color must be one of ${TASK_LIST_COLORS.join(', ')}.`);
  }
}
/** Unknown keys are refused, as `DisallowUnknownFields` refuses them. */
function refuseUnknown(input: object, allowed: readonly string[]): void {
  for (const key of Object.keys(input)) {
    if (!allowed.includes(key)) throw invalid(`json: unknown field "${key}"`);
  }
}

const MINUTE = 60_000;
const REMIND_STALE_MS = 15 * MINUTE;
const REMINDS: readonly TaskRemind[] = [...TASK_REMIND_TIMED, ...TASK_REMIND_DATE_ONLY];

function checkRemind(value: unknown): void {
  if (value === undefined || value === null) return;
  if (!(REMINDS as readonly unknown[]).includes(value)) {
    throw invalid(`remind must be one of ${REMINDS.join(', ')}.`);
  }
}

/** A reminder chosen for the wrong kind of due date (or none) is refused. */
function checkRemindFits(remind: TaskRemind | null, dueDate: string | null, dueTime: string | null): void {
  if (remind === null) return;
  if (dueDate === null) throw invalid('A reminder needs a due date.');
  const timed = (TASK_REMIND_TIMED as readonly string[]).includes(remind);
  if (timed !== (dueTime !== null)) {
    throw invalid(
      timed ? 'That reminder needs a due time.' : 'That reminder is for a task with a date and no time.',
    );
  }
}

/**
 * When the reminder rings and until when it is worth sending, in the
 * browser's zone. "1 day before" is the same wall time on the previous
 * calendar day (`Date` with a day of `d - 1` is DST-safe for that reason).
 */
function remindTimes(
  remind: TaskRemind | null,
  dueDate: string | null,
  dueTime: string | null,
): { at: number; until: number } | null {
  if (remind === null || dueDate === null) return null;
  const [y, m, d] = dueDate.split('-').map(Number) as [number, number, number];
  const [hh, mm] = (dueTime ?? '00:00').split(':').map(Number) as [number, number];
  const due = new Date(y, m - 1, d, hh, mm).getTime();
  let at: number;
  switch (remind) {
    case 'at_time':
      at = due;
      break;
    case '10m_before':
      at = due - 10 * MINUTE;
      break;
    case '1h_before':
      at = due - 60 * MINUTE;
      break;
    case '1d_before':
      at = new Date(y, m - 1, d - 1, hh, mm).getTime();
      break;
    case 'day_of_9am':
      at = new Date(y, m - 1, d, 9, 0).getTime();
      break;
    case 'day_before_9am':
      at = new Date(y, m - 1, d - 1, 9, 0).getTime();
      break;
  }
  const end = dueTime === null ? new Date(y, m - 1, d + 1).getTime() : due;
  return { at, until: Math.max(end, at + REMIND_STALE_MS) };
}

export function createMockTasksApi(options: {
  delay: (signal?: AbortSignal) => Promise<void>;
  newId: (prefix: string) => string;
  accountExists: (accountId: string) => boolean;
  seed?: readonly MockTaskSeed[];
  maxLists?: number;
  maxTasksPerList?: number;
  mail?: MockTaskMail;
}): TasksApi {
  const { delay, newId, accountExists } = options;
  const maxLists = options.maxLists ?? MOCK_MAX_TASK_LISTS;
  const maxTasks = options.maxTasksPerList ?? MOCK_MAX_TASKS_PER_LIST;
  const lists: ListRow[] = [];
  const tasks: TaskRow[] = [];
  const now = () => new Date().toISOString();

  const listsOf = (accountId: string) =>
    lists.filter((row) => row.accountId === accountId).sort((a, b) => a.position - b.position);
  const bySiblingOrder = (a: TaskRow, b: TaskRow) =>
    a.position - b.position || a.task.createdAt.localeCompare(b.task.createdAt);
  /** Siblings in order: the top-level tasks of a list (`parentId` null), or one parent's subtasks. */
  const siblingsOf = (listId: string, parentId: string | null) =>
    tasks
      .filter((row) => row.task.listId === listId && row.task.parentId === parentId)
      .sort(bySiblingOrder);
  const childrenOf = (row: TaskRow) =>
    tasks.filter((candidate) => candidate.task.parentId === row.task.id).sort(bySiblingOrder);
  /** Every task of a list: each top-level task immediately followed by its subtasks. */
  const tasksOf = (listId: string) =>
    siblingsOf(listId, null).flatMap((row) => [row, ...childrenOf(row)]);
  const rowById = (taskId: string | null) =>
    taskId === null ? undefined : tasks.find((row) => row.task.id === taskId);
  /** The wire copy: `parentTitle` is read from the parent as it is now. */
  const copyTask = (task: Task): Task => ({
    ...task,
    parentTitle: task.parentId === null ? null : (rowById(task.parentId)?.task.title ?? null),
    email: task.email === null ? null : { ...task.email },
  });
  /** Open, and not under a completed parent: what the smart views and reminders count. */
  const isOpen = (row: TaskRow) =>
    row.task.completedAt === null && (rowById(row.task.parentId)?.task.completedAt ?? null) === null;
  const setRemind = (row: TaskRow, remind: TaskRemind | null) => {
    const times = remindTimes(remind, row.task.dueDate, row.task.dueTime);
    row.task.remind = times ? remind : null;
    row.task.remindAt = times ? new Date(times.at).toISOString() : null;
    row.remindUntil = times ? times.until : null;
  };

  const requireList = (accountId: string, listId: string): ListRow => {
    const row = lists.find((r) => r.list.id === listId && r.accountId === accountId);
    if (!row) throw notFound('list');
    return row;
  };
  const requireTask = (accountId: string, taskId: string): TaskRow => {
    const row = tasks.find((r) => r.task.id === taskId && r.accountId === accountId);
    if (!row) throw notFound('task');
    return row;
  };
  const requireAccount = (accountId: string) => {
    if (!accountExists(accountId)) throw notFound('account');
  };

  const wireList = (row: ListRow): TaskList => {
    const own = tasks.filter((t) => t.task.listId === row.list.id);
    const completed = own.filter((t) => t.task.completedAt !== null).length;
    return { ...row.list, openCount: own.length - completed, completedCount: completed };
  };

  const touchTask = (row: TaskRow) => {
    row.task.version += 1;
    row.task.updatedAt = now();
  };

  /** Where to put something after `after` (undefined = first) among ordered `rows`. */
  function positionAfter<T extends { position: number }>(
    rows: T[],
    after: T | undefined,
    renumber: () => void,
  ): number {
    const index = after === undefined ? -1 : rows.indexOf(after);
    const previous = index >= 0 ? rows[index] : undefined;
    const next = rows[index + 1];
    if (previous === undefined && next === undefined) return 0;
    if (previous === undefined) return next!.position - STEP;
    if (next === undefined) return previous.position + STEP;
    if (next.position - previous.position >= 2) {
      return Math.floor((previous.position + next.position) / 2);
    }
    renumber();
    return Math.floor((previous.position + next.position) / 2);
  }
  const renumberRows = <T extends { position: number }>(rows: T[]) => {
    rows.forEach((row, index) => {
      row.position = (index + 1) * STEP;
    });
  };

  const stale = (current: unknown) =>
    fail(409, ApiErrorCodes.Conflict, 'This was changed somewhere else since you opened it.', {
      reason: 'stale',
      current: toWire(current),
    });
  const limit = (what: string, n: number) =>
    fail(409, ApiErrorCodes.Conflict, `${what} (${n}).`, { reason: 'limit', limit: n });

  function insertList(accountId: string, name: string, color: TaskListColor): ListRow {
    const own = listsOf(accountId);
    if (own.length >= maxLists) throw limit('You have reached the most lists a mailbox can have', maxLists);
    const stamp = now();
    const row: ListRow = {
      accountId,
      position: own.length === 0 ? 0 : own[own.length - 1]!.position + STEP,
      list: { id: newId('tl'), name, color, version: 1, createdAt: stamp, updatedAt: stamp },
    };
    lists.push(row);
    return row;
  }

  function insertTask(accountId: string, list: ListRow, input: TaskInput): TaskRow {
    const own = tasksOf(list.list.id);
    if (own.length >= maxTasks) throw limit('This list is full', maxTasks);
    const parentId = input.parentId ?? null;
    // New top-level tasks go on top; new subtasks at the bottom of their parent's.
    const siblings = siblingsOf(list.list.id, parentId);
    const position =
      siblings.length === 0
        ? 0
        : parentId === null
          ? siblings[0]!.position - STEP
          : siblings[siblings.length - 1]!.position + STEP;
    const stamp = now();
    const row: TaskRow = {
      accountId,
      position,
      remindUntil: null,
      task: {
        id: newId('task'),
        listId: list.list.id,
        title: input.title,
        notes: input.notes ?? '',
        dueDate: input.dueDate ?? null,
        dueTime: input.dueDate ? (input.dueTime ?? null) : null,
        starred: input.starred ?? false,
        completedAt: null,
        parentId,
        parentTitle: null,
        remind: null,
        remindAt: null,
        email: null,
        version: 1,
        createdAt: stamp,
        updatedAt: stamp,
      },
    };
    setRemind(row, input.remind ?? null);
    tasks.push(row);
    return row;
  }

  /** The server's handling of `email` on a create: validated, the Message-ID read from the mailbox when not sent. */
  function linkFor(accountId: string, input: { messageId: string; rfcMessageId?: string }): TaskEmailLink {
    refuseUnknown(input, ['messageId', 'rfcMessageId']);
    if (typeof input.messageId !== 'string' || input.messageId === '') {
      throw invalid('that email reference is not valid');
    }
    const sent = (input.rfcMessageId ?? '').trim().replace(/^<|>$/g, '');
    if (/[\s\p{Cc}]/u.test(sent) || [...sent].length > 998) throw invalid('that Message-ID is not valid');
    const found = options.mail?.byId(accountId, input.messageId);
    if (sent === '' && options.mail && !found) {
      throw fail(404, ApiErrorCodes.NotFound, 'that email is no longer where it was; refresh the list and try again');
    }
    return {
      accountId,
      folderId: found?.folderId ?? 'unknown-folder',
      messageId: input.messageId,
      rfcMessageId: sent !== '' ? sent : (found?.rfcMessageId ?? ''),
    };
  }

  for (const seed of options.seed ?? []) {
    const list = insertList(seed.accountId, seed.name, seed.color);
    // Seeded top-down: each insert goes to the top, so walk them backwards.
    for (const entry of [...seed.tasks].reverse()) {
      const { completed, ...input } = entry;
      const row = insertTask(seed.accountId, list, input);
      if (completed) row.task.completedAt = now();
    }
  }

  return {
    async lists(accountId, signal) {
      await delay(signal);
      requireAccount(accountId);
      return {
        lists: listsOf(accountId).map(wireList),
        maxLists,
        maxTasksPerList: maxTasks,
      };
    },

    async createList(accountId, input) {
      await delay();
      requireAccount(accountId);
      refuseUnknown(input, ['name', 'color']);
      const name = checkName(input.name);
      checkColor(input.color);
      return wireList(insertList(accountId, name, input.color ?? 'blue'));
    },

    async updateList(accountId, listId, patch) {
      await delay();
      refuseUnknown(patch, ['version', 'name', 'color']);
      const row = requireList(accountId, listId);
      if (patch.version !== row.list.version) throw stale(wireList(row));
      const name = patch.name === undefined ? undefined : checkName(patch.name);
      checkColor(patch.color);
      if (name !== undefined) row.list.name = name;
      if (patch.color !== undefined) row.list.color = patch.color;
      row.list.version += 1;
      row.list.updatedAt = now();
      return wireList(row);
    },

    async deleteList(accountId, listId) {
      await delay();
      const index = lists.findIndex((r) => r.list.id === listId && r.accountId === accountId);
      if (index < 0) return;
      lists.splice(index, 1);
      // ON DELETE CASCADE.
      for (let i = tasks.length - 1; i >= 0; i -= 1) {
        if (tasks[i]!.task.listId === listId) tasks.splice(i, 1);
      }
    },

    async moveList(accountId, listId, afterId) {
      await delay();
      const row = requireList(accountId, listId);
      const after = afterId === '' ? undefined : requireList(accountId, afterId);
      if (after === row) throw invalid('A list cannot go after itself.');
      const others = listsOf(accountId).filter((r) => r !== row);
      row.position = positionAfter(others, after, () => renumberRows(others));
      row.list.version += 1;
      row.list.updatedAt = now();
      return wireList(row);
    },

    async tasks(accountId, listId, signal) {
      await delay(signal);
      requireList(accountId, listId);
      return tasksOf(listId).map((row) => copyTask(row.task));
    },

    async smart(accountId, view, dueBefore, signal) {
      await delay(signal);
      requireAccount(accountId);
      if (view !== 'starred' && view !== 'due' && view !== 'reminders') {
        throw invalid('view must be starred, due or reminders.');
      }
      if (view === 'due') {
        if (!DATE.test(dueBefore)) throw invalid('due_before must be a date written YYYY-MM-DD.');
      }
      if (view === 'reminders') {
        // Reminders still worth sending, soonest first: the in-app fallback's read.
        const at = Date.now();
        return tasks
          .filter((r) => r.accountId === accountId && isOpen(r))
          .filter((r) => r.task.remindAt !== null && r.remindUntil !== null && r.remindUntil > at)
          .sort((a, b) => a.task.remindAt!.localeCompare(b.task.remindAt!))
          .slice(0, SMART_LIMIT)
          .map((r) => copyTask(r.task));
      }
      const listOrder = new Map(listsOf(accountId).map((r, index) => [r.list.id, index]));
      // Within a list, the list's own (flattened) order.
      const order = new Map<TaskRow, number>();
      for (const listRow of listsOf(accountId)) {
        tasksOf(listRow.list.id).forEach((r, index) => order.set(r, index));
      }
      return tasks
        .filter((r) => r.accountId === accountId && isOpen(r))
        .filter((r) =>
          view === 'starred'
            ? r.task.starred
            : r.task.dueDate !== null && r.task.dueDate <= dueBefore,
        )
        .sort(
          (a, b) =>
            compareNullsLast(a.task.dueDate, b.task.dueDate) ||
            compareNullsFirst(a.task.dueTime, b.task.dueTime) ||
            (listOrder.get(a.task.listId) ?? 0) - (listOrder.get(b.task.listId) ?? 0) ||
            (order.get(a) ?? 0) - (order.get(b) ?? 0),
        )
        .slice(0, SMART_LIMIT)
        .map((r) => copyTask(r.task));
    },

    async createTask(accountId, listId, input) {
      await delay();
      refuseUnknown(input, ['title', 'notes', 'dueDate', 'dueTime', 'starred', 'parentId', 'remind', 'email']);
      const list = requireList(accountId, listId);
      const title = checkTitle(input.title);
      checkNotes(input.notes ?? '');
      checkDate(input.dueDate);
      checkTime(input.dueTime);
      if (input.dueTime && !input.dueDate) throw invalid('A time needs a date.');
      checkRemind(input.remind);
      checkRemindFits(
        input.remind ?? null,
        input.dueDate ?? null,
        input.dueDate ? (input.dueTime ?? null) : null,
      );
      if (input.parentId !== undefined) {
        const parent = requireTask(accountId, input.parentId);
        if (parent.task.listId !== listId) throw invalid('A subtask must be in its parent’s list.');
        if (parent.task.parentId !== null) throw invalid('A subtask cannot have subtasks of its own.');
      }
      const email = input.email === undefined ? null : linkFor(accountId, input.email);
      const row = insertTask(accountId, list, { ...input, title });
      row.task.email = email;
      return copyTask(row.task);
    },

    async updateTask(accountId, taskId, patch: TaskPatch) {
      await delay();
      refuseUnknown(patch, ['version', 'title', 'notes', 'dueDate', 'dueTime', 'starred', 'remind']);
      const row = requireTask(accountId, taskId);
      if (patch.version !== row.task.version) throw stale(copyTask(row.task));
      const title = patch.title === undefined ? undefined : checkTitle(patch.title);
      if (patch.notes !== undefined) checkNotes(patch.notes);
      checkDate(patch.dueDate);
      checkTime(patch.dueTime);
      const nextDate = patch.dueDate === undefined ? row.task.dueDate : patch.dueDate;
      let nextTime = patch.dueTime === undefined ? row.task.dueTime : patch.dueTime;
      // A cleared date clears its time: a time on no day means nothing.
      if (patch.dueDate === null) nextTime = null;
      if (nextTime !== null && nextDate === null) throw invalid('A time needs a date.');
      checkRemind(patch.remind);
      let nextRemind: TaskRemind | null;
      if (patch.remind !== undefined) {
        nextRemind = patch.remind;
        checkRemindFits(nextRemind, nextDate, nextTime);
      } else if (nextDate === null || row.task.remind === null) {
        // A cleared date clears its reminder.
        nextRemind = null;
      } else {
        // Not named: kept, or mapped when the due kind changed.
        nextRemind = mapRemindForDueKind(row.task.remind, nextTime !== null);
      }
      if (title !== undefined) row.task.title = title;
      if (patch.notes !== undefined) row.task.notes = patch.notes;
      if (patch.starred !== undefined) row.task.starred = patch.starred;
      row.task.dueDate = nextDate;
      row.task.dueTime = nextTime;
      setRemind(row, nextRemind);
      touchTask(row);
      return copyTask(row.task);
    },

    async deleteTask(accountId, taskId) {
      await delay();
      const index = tasks.findIndex((r) => r.task.id === taskId && r.accountId === accountId);
      if (index < 0) return;
      tasks.splice(index, 1);
      // ON DELETE CASCADE: a parent's subtasks go with it.
      for (let i = tasks.length - 1; i >= 0; i -= 1) {
        if (tasks[i]!.task.parentId === taskId) tasks.splice(i, 1);
      }
    },

    async completeTask(accountId, taskId) {
      await delay();
      const row = requireTask(accountId, taskId);
      if (row.task.completedAt === null) {
        row.task.completedAt = now();
        touchTask(row);
      }
      return copyTask(row.task);
    },

    async uncompleteTask(accountId, taskId) {
      await delay();
      const row = requireTask(accountId, taskId);
      if (row.task.completedAt !== null) {
        row.task.completedAt = null;
        touchTask(row);
      }
      // A subtask reopened under a completed parent brings the parent back
      // too, or it would be open and nowhere to be seen.
      const parent = rowById(row.task.parentId);
      if (parent && parent.task.completedAt !== null) {
        parent.task.completedAt = null;
        touchTask(parent);
      }
      return copyTask(row.task);
    },

    async moveTask(accountId, taskId, input) {
      await delay();
      refuseUnknown(input, ['listId', 'afterId', 'parentId']);
      const row = requireTask(accountId, taskId);
      const target = requireList(accountId, input.listId ?? row.task.listId);
      const crossList = target.list.id !== row.task.listId;
      // Absent: keep the parent within the list; top-level in another list.
      const parentId =
        input.parentId === undefined ? (crossList ? null : row.task.parentId) : input.parentId;
      const children = childrenOf(row);
      if (parentId !== null) {
        if (parentId === row.task.id) throw invalid('A task cannot be its own subtask.');
        const parent = requireTask(accountId, parentId);
        if (parent.task.listId !== target.list.id) throw invalid('A subtask must be in its parent’s list.');
        if (parent.task.parentId !== null) throw invalid('A subtask cannot have subtasks of its own.');
        if (children.length > 0) throw invalid('A task with subtasks cannot become a subtask.');
      }
      const others = siblingsOf(target.list.id, parentId).filter((r) => r !== row);
      const after =
        input.afterId === '' ? undefined : others.find((r) => r.task.id === input.afterId);
      if (input.afterId !== '' && after === undefined) throw notFound('task');
      if (crossList && tasksOf(target.list.id).length + 1 + children.length > maxTasks) {
        throw limit('That list is full', maxTasks);
      }
      row.position = positionAfter(others, after, () => renumberRows(others));
      row.task.listId = target.list.id;
      row.task.parentId = parentId;
      // ON UPDATE CASCADE: a parent's subtasks follow it to the other list.
      for (const child of children) child.task.listId = target.list.id;
      touchTask(row);
      return copyTask(row.task);
    },

    async byEmail(accountId, email, signal) {
      await delay(signal);
      const rfc = email.rfcMessageId ?? '';
      const id = email.messageId ?? '';
      if (rfc === '' && id === '') throw invalid('name the email by rfc_message_id or message_id');
      const done = (row: TaskRow) => !isOpen(row);
      return tasks
        .filter((row) => row.accountId === accountId && row.task.email !== null)
        .filter(
          (row) =>
            (rfc !== '' && row.task.email!.rfcMessageId === rfc) || (id !== '' && row.task.email!.messageId === id),
        )
        .sort(
          (a, b) =>
            Number(done(a)) - Number(done(b)) || b.task.createdAt.localeCompare(a.task.createdAt),
        )
        .slice(0, 20)
        .map((row) => copyTask(row.task));
    },

    async resolveEmail(accountId, taskId, signal): Promise<TaskEmailResolution> {
      await delay(signal);
      const row = requireTask(accountId, taskId);
      const link = row.task.email;
      if (link === null) throw fail(404, ApiErrorCodes.NotFound, 'this task is not linked to an email');
      const mail = options.mail;
      const here = mail?.byId(accountId, link.messageId);
      if (here && here.folderId === link.folderId && (link.rfcMessageId === '' || here.rfcMessageId === link.rfcMessageId)) {
        return { status: 'found', messageId: here.messageId, folderId: here.folderId, folderRole: here.folderRole };
      }
      const moved = link.rfcMessageId === '' ? undefined : mail?.byRfcId(accountId, link.rfcMessageId);
      if (!moved) return { status: 'gone', messageId: null, folderId: null, folderRole: null };
      // The link now points where it was found; no version bump (a cache of where the mail is).
      row.task.email = { ...link, messageId: moved.messageId, folderId: moved.folderId };
      return { status: 'moved', messageId: moved.messageId, folderId: moved.folderId, folderRole: moved.folderRole };
    },

    async clearCompleted(accountId, listId) {
      await delay();
      requireList(accountId, listId);
      let deleted = 0;
      for (let i = tasks.length - 1; i >= 0; i -= 1) {
        const row = tasks[i]!;
        if (row.task.listId === listId && row.task.completedAt !== null) {
          tasks.splice(i, 1);
          deleted += 1;
        }
      }
      // ON DELETE CASCADE: the subtasks of a cleared parent go with it.
      const remaining = new Set(tasks.map((row) => row.task.id));
      for (let i = tasks.length - 1; i >= 0; i -= 1) {
        const parentId = tasks[i]!.task.parentId;
        if (parentId !== null && !remaining.has(parentId)) tasks.splice(i, 1);
      }
      return { deleted };
    },
  };
}

function compareNullsLast(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a < b ? -1 : 1;
}
function compareNullsFirst(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return a < b ? -1 : 1;
}
